import { Link } from "react-router-dom";
import type { Vehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface HostListingCardProps {
  vehicle: Vehicle;
  bookingsCount: number;
  toggling: boolean;
  onToggle: (vehicleId: number, available: boolean) => void;
}

export default function HostListingCard({ vehicle, bookingsCount, toggling, onToggle }: HostListingCardProps) {
  const image = vehicle.images?.[0]?.url || FALLBACK_IMAGE;
  const title = vehicleTitle(vehicle.make, vehicle.model, vehicle.year);
  const available = vehicle.available !== false;

  return (
    <article className="overflow-hidden rounded-2xl surface-card">
      <div className="relative h-40 w-full overflow-hidden bg-background-100">
        <img src={image} alt={`${title} listing`} title={`${title} — your Gari listing`} className="h-full w-full object-cover object-top" />
        <span
          className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
            available ? "bg-accent-500/90 text-background-50" : "bg-background-50/85 text-foreground-900 backdrop-blur"
          }`}
        >
          <i className={available ? "ri-checkbox-circle-line" : "ri-pause-circle-line"}></i>
          {available ? "Live" : "Paused"}
        </span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-heading text-base font-semibold text-foreground-950">{title}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-map-pin-line text-foreground-500"></i>
              <span className="truncate">{vehicle.location || vehicle.city}</span>
            </p>
          </div>
          <span className="shrink-0 rounded-md bg-secondary-100/70 px-2 py-1 text-xs font-medium text-secondary-900">
            {vehicle.category}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-calendar-check-line"></i> {bookingsCount} {bookingsCount === 1 ? "booking" : "bookings"}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-star-fill text-primary-500"></i> {Number(vehicle.rating ?? 0).toFixed(2)}
          </span>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3">
          <p>
            <span className="font-heading text-lg font-semibold text-foreground-950">{formatKES(vehicle.price_per_day)}</span>
            <span className="text-xs text-foreground-500"> /day</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={toggling}
              onClick={() => onToggle(vehicle.id, !available)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-900 transition-colors hover:border-primary-500/60 disabled:opacity-50"
            >
              {toggling ? <i className="ri-loader-4-line animate-spin"></i> : <i className={available ? "ri-pause-line" : "ri-play-line"}></i>}
              {available ? "Pause" : "Resume"}
            </button>
            <Link
              to={`/vehicle/${vehicle.id}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-eye-line"></i> View
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}